// src/services/receivableService.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import dayjs from 'dayjs';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import orderService from 'services/orderService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';
import { logErrorSilent } from 'utils/errorLogger';

// MAIN SERVICE ***************************************************************
// *****************************************************************************

const receivableService = {};

// Create a receivable from a generated order and generate the asaas payment link.
// Flow: GET order + GET persisted buyer entity -> POST /v1/receivables ->
// POST /asaas/payments (apiKey resolved server-side) -> PUT /v1/receivables/:_id
// with the returned asaas fields. Returns the updated receivable (or null).
receivableService.generateBillFromOrder = async (
  orderId,
  buyerId,
  billingType,
  dueDate = dayjs().endOf('day'),
  hideMessages = false,
) => {
  if (!orderId || !buyerId) return null;

  const order = await orderService.getOrderById(orderId);

  // the PERSISTED buyer entity already has the nested documents[]/contacts[] shape
  // the backend customerMapper reads (Step 2 of checkout upserted it)
  const buyerUrl = API.ENTITIES.DETAIL.replace(':_id', buyerId);
  const [buyer] = await axiosService.get({ url: buyerUrl, returnAsObject: true });

  // totalInOrderDate is the frozen pricing waterfall OBJECT (.totalToPay / .paymentInstallment)
  const total = order?.totalInOrderDate || {};

  const travellersCount = order?.travellers?.length || 0;
  const destination = `${translateX(order?.destiny)} (${dayjs(order?.departure).format(
    'L',
  )} - ${dayjs(order?.arrival).format('L')}) | ${travellersCount} ${
    travellersCount === 1 ? 'Viajante' : 'Viajantes'
  }`;
  const orderLabel = order?._metadata?.__documentCode || order?.order || '';
  const operatorName = order?.__operator?.name ? ` | ${order.__operator.name}` : '';
  const planName = order?.__plan?.name ? ` | ${order.__plan.name}` : '';
  const description = `Pedido No ${orderLabel} | ${destination}${operatorName}${planName}`;

  const body = {
    billingType,

    value: total?.totalToPay,
    valueCurrency: 'brl',
    dueDate,

    // bankCode/agencyCode/accountCode omitted — v5 has no bank module (parity divergence #2)

    description,
    installment: 1,
    installmentTotal: total?.paymentInstallment || 1,

    // FINE AND INTEREST
    fineValue: 0,
    finePercentage: 0,
    fineType: 'percentage',

    interestValue: 0,
    interestPercentage: 0,
    interestType: 'percentage',

    discountValue: 0,
    discountPercentage: 0,
    discountType: 'percentage',

    discountExpireDate: null,

    // CUSTOMER
    entityId: buyer?._id || buyerId,

    orderId,
    order: order?.order, // order number
  };

  try {
    // 1) persist the receivable (status 'new', empty asaas fields)
    const [receivable] = await axiosService.post({ url: API.RECEIVABLES.URL.replace(':_id?', ''), data: body });
    if (!receivable?._id) throw new Error('receivable_not_created');

    // 2) create the asaas payment — the backend resolves the apiKey from the
    //    authenticated subscription and maps billingType/customer server-side
    const [asaasData] = await axiosService.post({
      url: API.ASAAS.PAYMENTS,
      data: { receivable, entity: buyer },
    });

    // 3) persist the asaas fields back onto the receivable
    const detailUrl = API.RECEIVABLES.DETAIL.replace(':_id', receivable._id);
    const { asaasCustomerId, ...asaasFields } = asaasData || {};
    const [updated] = await axiosService.put({
      url: detailUrl,
      data: { ...asaasFields, status: 'waiting_for_payment' },
    });

    if (!hideMessages) {
      appService.message({ flag: 's', text: 'payment_link_generated_success' });
    }

    return updated || { ...receivable, ...asaasFields, status: 'waiting_for_payment' };
  } catch (error) {
    if (!hideMessages) {
      appService.notification({
        flag: 'e',
        title: 'payment',
        description: error?.response?.data?.message || 'payment_link_generated_failure',
        duration: 0,
      });
    }
    throw error;
  }
};

// Generate an Asaas payment link for an EXISTING receivable (standalone bill).
// Mirrors backup ReceivablesFormHeaderColumn1 "generate_billing", adapted to the
// v5 server-side asaas orchestration (apiKey resolved server-side — NO apiKey sent).
receivableService.generatePaymentLink = async (receivable, hideMessages = false) => {
  if (!receivable?._id || !receivable?.entityId) {
    if (!hideMessages) {
      appService.message({ flag: 'w', text: 'payment_link_missing_data' });
    }
    return null;
  }

  try {
    // 1. fetch the persisted customer entity (nested documents[]/contacts[] shape)
    const entityUrl = API.ENTITIES.DETAIL.replace(':_id', receivable.entityId);
    const [entity] = await axiosService.get({ url: entityUrl, returnAsObject: true });

    // 2. ask the backend to create the asaas charge (server resolves apiKey)
    const [asaas] = await axiosService.post({
      url: API.ASAAS.PAYMENTS,
      data: { receivable, entity },
    });

    if (!asaas) {
      if (!hideMessages) {
        appService.message({ flag: 'e', text: 'payment_link_generated_failure' });
      }
      return null;
    }

    // 3. persist the returned asaas fields onto the receivable (minus asaasCustomerId)
    const { asaasCustomerId: _drop, ...asaasFields } = asaas;
    const putUrl = API.RECEIVABLES.DETAIL.replace(':_id', receivable._id);
    const [updated] = await axiosService.put({
      url: putUrl,
      data: { ...asaasFields, status: 'waiting_for_payment' },
    });

    if (!hideMessages) {
      appService.message({ flag: 's', text: 'payment_link_generated_success' });
    }
    return updated;
  } catch (error) {
    logErrorSilent(error, { context: 'receivableService.generatePaymentLink' });
    if (!hideMessages) {
      appService.message({ flag: 'e', text: 'payment_link_generated_failure' });
    }
    return null;
  }
};

// EXPORT *********************************************************************
// *****************************************************************************

export default receivableService;
